// store/forms/samplingEventForm/samplingGroups/samplingEventFormGroupsActions.js
// Actions for managing sampling group membership on an active sampling event:
// adding employees to a group, moving them between groups, and removing them.

import { v4 as uuidv4 } from "uuid";
import { SamplingGroupEmployees } from "../../../../models/SamplingEvent/SamplingGroup";

// Factory for sampling group actions. Mirrors the other slice factories
// (makeWellsActions, makeDailySummaryActions) and uses the shared `api` shim.
export const makeGroupsActions = (api) => {
  // Locate a group employee row by its id across every group in the event.
  // Returns { group, employee, index } or null.
  const findGroupEmployee = (event, rowId) => {
    for (const group of event.samplingGroups || []) {
      const index = group.employees.findIndex((e) => e.id === rowId);
      if (index >= 0) {
        return { group, employee: group.employees[index], index };
      }
    }
    return null;
  };

  return {
    /**
     * Add one or more directory employees to a sampling group. Each employee
     * is optimistically pushed to the group's employees array and an "add"
     * patch is emitted per employee.
     *
     * @param {object} params
     * @param {string} params.samplingGroupId - The group to add employees to
     * @param {Array<object>} params.employees - Directory employees ({ employee_id, label })
     * @param {function} params.emitPatch - Autosave emitPatch from tabProps
     * @returns {Promise<{ok: boolean, added: number, error?: string}>}
     */
    addGroupEmployees: async ({ samplingGroupId, employees, emitPatch }) => {
      const event = api.getCurrentEvent();
      if (!event?.id) return { ok: false, error: "no_event" };
      if (!samplingGroupId) return { ok: false, error: "no_group_selected" };
      if (!Array.isArray(employees) || employees.length === 0) {
        return { ok: false, error: "no_employees_selected" };
      }

      const group = event.samplingGroups.find((g) => g.id === samplingGroupId);
      if (!group) return { ok: false, error: "group_not_found" };

      // Employees already assigned to any group in this event (one group per employee).
      const assignedIds = new Set();
      (event.samplingGroups || []).forEach((g) => {
        g.employees.forEach((e) => {
          if (e.employeeId) assignedIds.add(e.employeeId);
        });
      });

      let added = 0;
      for (const directoryEmployee of employees) {
        const employeeId = directoryEmployee.employee_id;
        if (!employeeId || assignedIds.has(employeeId)) continue;
        assignedIds.add(employeeId);

        const newRowId = uuidv4();
        const newEmployee = new SamplingGroupEmployees({
          id: newRowId,
          sampling_group_id: samplingGroupId,
          employee_id: employeeId,
          employee_name:
            directoryEmployee.label || directoryEmployee.name || "",
        });
        group.employees.push(newEmployee);
        api.bumpRevision();

        if (typeof emitPatch === "function") {
          try {
            const result = await emitPatch({
              patch_type: "sampling_group_employees",
              action: "add",
              sampling_event_id: event.id,
              id: newRowId,
              sampling_group_id: samplingGroupId,
              employee_id: employeeId,
            });
            const isSuccess =
              result === true ||
              result?.success === true ||
              result?.ok === true;
            if (isSuccess) added += 1;
          } catch (err) {
            console.error("Error emitting add group employee patch:", err);
          }
        }
      }

      return { ok: true, added };
    },

    /**
     * Move a group employee row to a different group.
     *
     * @param {object} params
     * @param {string} params.rowId - The SamplingGroupEmployees row id
     * @param {string} params.toGroupId - The destination group id
     * @param {function} params.emitPatch - Autosave emitPatch from tabProps
     * @returns {Promise<{ok: boolean, error?: string}>}
     */
    moveGroupEmployee: async ({ rowId, toGroupId, emitPatch }) => {
      const event = api.getCurrentEvent();
      if (!event?.id) return { ok: false, error: "no_event" };
      if (!toGroupId) return { ok: false, error: "no_target_group" };

      const found = findGroupEmployee(event, rowId);
      if (!found) return { ok: false, error: "employee_not_found" };
      if (found.group.id === toGroupId) return { ok: true };

      const targetGroup = event.samplingGroups.find((g) => g.id === toGroupId);
      if (!targetGroup) return { ok: false, error: "target_group_not_found" };

      // Move the row: splice from source, update its group id, push to target.
      found.group.employees.splice(found.index, 1);
      found.employee.samplingGroupId = toGroupId;
      targetGroup.employees.push(found.employee);
      api.bumpRevision();

      if (typeof emitPatch === "function") {
        try {
          const result = await emitPatch({
            patch_type: "sampling_group_employees",
            action: "update",
            sampling_event_id: event.id,
            id: rowId,
            sampling_group_id: toGroupId,
            employee_id: found.employee.employeeId,
          });
          const isSuccess =
            result === true || result?.success === true || result?.ok === true;
          return { ok: isSuccess };
        } catch (err) {
          console.error("Error emitting move group employee patch:", err);
          return { ok: false, error: "emit_failed" };
        }
      }

      return { ok: true };
    },

    /**
     * Remove a group employee row entirely.
     *
     * @param {object} params
     * @param {string} params.rowId - The SamplingGroupEmployees row id
     * @param {function} params.emitPatch - Autosave emitPatch from tabProps
     * @returns {Promise<{ok: boolean, error?: string}>}
     */
    removeGroupEmployee: async ({ rowId, emitPatch }) => {
      const event = api.getCurrentEvent();
      if (!event?.id) return { ok: false, error: "no_event" };

      const found = findGroupEmployee(event, rowId);
      if (!found) return { ok: false, error: "employee_not_found" };

      const samplingGroupId = found.group.id;
      const employeeId = found.employee.employeeId;
      found.group.employees.splice(found.index, 1);
      api.bumpRevision();

      if (typeof emitPatch === "function") {
        try {
          const result = await emitPatch({
            patch_type: "sampling_group_employees",
            action: "remove",
            sampling_event_id: event.id,
            id: rowId,
            sampling_group_id: samplingGroupId,
            employee_id: employeeId,
          });
          const isSuccess =
            result === true || result?.success === true || result?.ok === true;
          return { ok: isSuccess };
        } catch (err) {
          console.error("Error emitting remove group employee patch:", err);
          return { ok: false, error: "emit_failed" };
        }
      }

      return { ok: true };
    },

    /**
     * Handle an incoming sampling_group_employees broadcast from another client.
     * @param {object} patch - { action, id, sampling_group_id, employee_id, employee_name }
     */
    handleBroadcast: (patch) => {
      const event = api.getCurrentEvent();
      if (!event) return;

      const { action, id } = patch || {};
      if (!id) return;

      if (action === "remove") {
        const found = findGroupEmployee(event, id);
        if (found) {
          found.group.employees.splice(found.index, 1);
          api.bumpRevision();
        }
        return;
      }

      if (action === "add") {
        const targetGroup = event.samplingGroups.find(
          (g) => g.id === patch.sampling_group_id
        );
        if (!targetGroup) return;
        const exists = targetGroup.employees.some((e) => e.id === id);
        if (!exists) {
          targetGroup.employees.push(
            new SamplingGroupEmployees({
              id,
              sampling_group_id: patch.sampling_group_id,
              employee_id: patch.employee_id,
              employee_name: patch.employee_name || "",
            })
          );
          api.bumpRevision();
        }
        return;
      }

      if (action === "update") {
        const found = findGroupEmployee(event, id);
        if (!found) return;
        const targetGroup = event.samplingGroups.find(
          (g) => g.id === patch.sampling_group_id
        );
        if (!targetGroup) return;
        if (found.group.id !== targetGroup.id) {
          found.group.employees.splice(found.index, 1);
          found.employee.samplingGroupId = targetGroup.id;
          targetGroup.employees.push(found.employee);
        }
        if (patch.employee_name) {
          found.employee.employeeName = patch.employee_name;
        }
        api.bumpRevision();
      }
    },
  };
};
